<script setup>
import { Pie, Bar, Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement
} from 'chart.js';

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement
);

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: (value) => ['pie', 'bar', 'line', 'donut'].includes(value)
  },
  data: {
    type: Array,
    required: true
  },
  colors: {
    type: Array,
    default: () => ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4']
  },
  width: {
    type: Number,
    default: 400
  },
  height: Number,
  id: {
    type: String,
    required: false
  },
  /**
   * If true and type is 'bar', renders a horizontal bar chart (indexAxis: 'y').
   */
  horizontal: {
    type: Boolean,
    default: false
  }
});

const chartData = computed(() => {
  if (props.type === 'pie' || props.type === 'donut') {
    return {
      labels: props.data.map(d => d.name || d.label),
      datasets: [
        {
          data: props.data.map(d => d.value),
          backgroundColor: props.data.map((d, i) => d.color || props.colors[i % props.colors.length]),
        }
      ]
    };
  } else if (props.type === 'bar') {
    return {
      labels: props.data.map(d => d.label),
      datasets: [
        {
          label: 'Inquiries',
          data: props.data.map(d => d.value),
          backgroundColor: props.data.map((d, i) => d.color || props.colors[i % props.colors.length]),
        }
      ]
    };
  } else if (props.type === 'line') {
    // Support for single or dual line (submitted/completed)
    const hasCompleted = props.data.some(d => d.completed !== undefined);
    return {
      labels: props.data.map(d => d.month || d.label),
      datasets: [
        {
          label: 'Submitted',
          data: props.data.map(d => d.count || d.value),
          borderColor: props.colors[0],
          backgroundColor: props.colors[0],
          fill: false,
          tension: 0.3
        },
        ...(hasCompleted ? [{
          label: 'Completed',
          data: props.data.map(d => d.completed || 0),
          borderColor: props.colors[1],
          backgroundColor: props.colors[1],
          fill: false,
          tension: 0.3
        }] : [])
      ]
    };
  }
  return {};
});

const chartOptions = computed(() => {
  if (props.type === 'pie') {
    return {
      responsive: true,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true },
      }
    };
  } else if (props.type === 'donut') {
    return {
      responsive: true,
      cutout: '60%', // This makes it a donut chart
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true },
      }
    };
  } else if (props.type === 'bar') {
    const isHorizontal = props.horizontal === true;
    return {
      responsive: true,
      indexAxis: isHorizontal ? 'y' : 'x',
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true },
      },
      scales: isHorizontal
        ? {
          x: {
            ticks: {
              color: '#6B7280',
              font: { size: 12 },
              stepSize: 1,
              precision: 0,
              callback: function (value) { return Number(value).toFixed(0); }
            },
            beginAtZero: true
          },
          y: {
            ticks: { color: '#6B7280', font: { size: 12 } }
          }
        }
        : {
          x: { ticks: { color: '#6B7280', font: { size: 12 } } },
          y: {
            ticks: {
              color: '#6B7280',
              font: { size: 12 },
              stepSize: 1,
              precision: 0,
              callback: function (value) { return Number(value).toFixed(0); }
            },
            beginAtZero: true
          }
        }
    };
  } else if (props.type === 'line') {
    return {
      responsive: true,
      plugins: {
        legend: { display: true },
        tooltip: { enabled: true },
      },
      scales: {
        x: { ticks: { color: '#6B7280', font: { size: 12 } } },
        y: { ticks: { color: '#6B7280', font: { size: 12 } }, beginAtZero: true }
      }
    };
  }
  return {};
});
</script>

<template>
  <div :id="id" class="w-full h-full flex justify-center items-center">
    <Pie v-if="type === 'pie' || type === 'donut'" :data="chartData" :options="chartOptions" :width="width"
      :height="height" />
    <Bar v-else-if="type === 'bar'" :data="chartData" :options="chartOptions" :width="width" :height="height" />
    <Line v-else-if="type === 'line'" :data="chartData" :options="chartOptions" :width="width" :height="height" />
  </div>
</template>

<style scoped>
/* Add any chart-specific styles here */
</style>